import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {Box, Grid} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {TrendingUpOutlined, CurrencyYuanOutlined, OutlinedFlagOutlined, TrendingDownOutlined} from '@mui/icons-material';
import {lineElementClasses, LinePlot} from "@mui/x-charts/LineChart";
import {ChartsXAxis, ChartContainer, ChartsTooltip, ChartsYAxis} from '@mui/x-charts';
import {useMemo, useState} from "react";
import {sumBy} from "lodash";
import {XYVO} from "@/service/analyze";
import {KeyboardArrowUpOutlined} from '@mui/icons-material';
import {ChartsLoadingOverlay} from "@mui/x-charts/ChartsOverlay";

export interface OverallBalanceProps {
    recentYearCustomers?: XYVO[]
    loading: boolean
}

export default function OverallBalance({recentYearCustomers, loading}: OverallBalanceProps) {
    const [tab, setTab] = useState<number>(1)
    const {totalLead, totalCustomer, totalDeal, totalChurn} = useMemo(() => {
        const totalDeal: number = sumBy(recentYearCustomers, deal => parseInt(deal.y2)) || 0
        const totalLead: number = sumBy(recentYearCustomers, lead => parseInt(lead.y1)) || 0
        const totalChurn: number = sumBy(recentYearCustomers, churn => parseInt(churn.y3)) || 0
        return {totalLead, totalDeal, totalCustomer: totalLead + totalDeal + totalChurn, totalChurn}
    }, [recentYearCustomers])

    return (
        <Card variant="outlined" sx={{width: '100%', height: 320}}>
            <CardContent>
                <Grid container direction="row" spacing={2}>
                    <Grid size={8}>
                        <Stack rowGap={0.6} sx={{justifyContent: 'space-between'}}>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Stack rowGap={0.6} direction={'column'}>
                                    <Typography component="h2" variant="subtitle2" gutterBottom>
                                        总体情况
                                    </Typography>
                                    {/*<Stack*/}
                                    {/*    direction="row"*/}
                                    {/*    sx={{*/}
                                    {/*        alignContent: {xs: 'center', sm: 'flex-start'},*/}
                                    {/*        alignItems: 'center',*/}
                                    {/*        gap: 1,*/}
                                    {/*    }}*/}
                                    {/*>*/}
                                    {/*    <Typography variant="h5" component="p">*/}
                                    {/*        {tab == 1 && totalLead || tab == 2 && totalDeal || tab == 3 && totalChurn || 0}*/}
                                    {/*    </Typography>*/}
                                    {/*    <Chip size="small" color="error" label="-8%"/>*/}
                                    {/*</Stack>*/}
                                    {/*<Stack direction="row" columnGap={2}>*/}
                                    {/*    <Chip icon={<KeyboardArrowUpOutlined/>} label="24.9%" color='success'/>*/}
                                    {/*    <Typography variant="caption" sx={{color: 'text.secondary'}}>*/}
                                    {/*        最近12个月*/}
                                    {/*    </Typography>*/}
                                    {/*</Stack>*/}
                                </Stack>
                                <ToggleButtonGroup
                                    color="primary"
                                    size={'small'}
                                    value={tab}
                                    onChange={(_, value: number) => value && setTab(value)}
                                    sx={{
                                        height: '32px',
                                        boxShadow: 'none', "& .MuiButtonBase": {
                                            padding: '4px 12px',
                                        },
                                    }}
                                    exclusive
                                    aria-label="Platform"
                                >
                                    <ToggleButton sx={{
                                        border: 'none', backgroundColor: '#f5f5f4'
                                    }} size={'small'} value={1}>意向客户</ToggleButton>
                                    <ToggleButton sx={{
                                        border: 'none', backgroundColor: '#f5f5f4'
                                    }} size={'small'} value={2}>成交客户</ToggleButton>
                                    <ToggleButton sx={{
                                        border: 'none', backgroundColor: '#f5f5f4'
                                    }} size={'small'} value={3}>流失客户</ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                            <ChartContainer
                                height={250}
                                localeText={{loading: loading ? "加载中" : ''}}
                                margin={{top: 0, left: 0, right: 10, bottom: 0}}
                                series={[
                                    {type: "line", color: tab == 1 ? '#06a1fb' : "#e4e9f1", data: recentYearCustomers?.map(item => item.y1) || []},
                                    {
                                        color: tab == 2 ? '#06a1fb' : "#e4e9f1",
                                        type: "line",
                                        data: recentYearCustomers?.map(item => item.y2) || [],
                                    },
                                    {
                                        color: tab == 3 ? '#06a1fb' : "#e4e9f1",
                                        type: "line",
                                        data: recentYearCustomers?.map(item => item.y3) || [],
                                    }
                                ]}
                                yAxis={[{width: 40, valueFormatter: (value: number) => `${value}个`}]}
                                xAxis={[{
                                    tickLabelInterval: (value, index) => index % 2 == 0, valueFormatter: value => `${value}月`,
                                    scaleType: "point", data: recentYearCustomers?.map(item => item.x) || []
                                }]}
                                sx={{
                                    [`& .${lineElementClasses.root}`]: {
                                        strokeWidth: 5,
                                    }
                                }}
                                // disableAxisListener={true}
                            >
                                <LinePlot/>
                                <ChartsTooltip/>
                                <ChartsYAxis disableTicks={true} disableLine={true}/>
                                <ChartsLoadingOverlay/>
                                <ChartsXAxis disableLine={true} disableTicks={true}/>
                            </ChartContainer>
                        </Stack>
                    </Grid>
                    <Grid size={4}>
                        <Stack direction={'column'} justifyContent={'space-between'} height={'100%'} alignContent={'space-between'}>
                            <Box component={'div'} bgcolor={'#eff4fa'} p={'9px'} borderRadius={'12px'}>
                                <Stack direction={'row'} gap={2} alignContent={'center'} alignItems={'center'}>
                                    <IconButton sx={{backgroundColor: '#fff1cc', padding: '6px 14px', borderRadius: '9999px'}} size={'medium'}>
                                        <TrendingUpOutlined sx={{color: '#ffae1f'}}/>
                                    </IconButton>
                                    <Stack>
                                        <Typography component="p" variant='caption' fontSize={'12px'} color={'#7b8893'}>
                                            全部客户
                                        </Typography>
                                        <Typography component="h6" variant='subtitle2' fontWeight={600}>
                                            {totalCustomer}个
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                            <Box component={'div'} bgcolor={'#eff4fa'} p={'9px'} borderRadius={'12px'}>
                                <Stack direction={'row'} gap={2} alignContent={'center'} alignItems={'center'}>
                                    <IconButton sx={{backgroundColor: '#ffe4ec', padding: '8px 14px', borderRadius: '9999px'}} size={'medium'}>
                                        <CurrencyYuanOutlined sx={{color: '#ff6692'}}/>
                                    </IconButton>
                                    <Stack>
                                        <Typography component="p" variant='caption' fontSize={'12px'} color={'#7b8893'}>
                                            意向客户
                                        </Typography>
                                        <Typography component="h6" variant='subtitle2' fontWeight={600}>
                                            {totalLead}个
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                            <Box component={'div'} bgcolor={'#eff4fa'} p={'9px'} borderRadius={'12px'}>
                                <Stack direction={'row'} gap={2} alignContent={'center'} alignItems={'center'}>
                                    <IconButton sx={{backgroundColor: '#d2f9f4', padding: '8px 14px', borderRadius: '9999px'}} size={'large'}>
                                        <OutlinedFlagOutlined sx={{color: '#00ceb6'}}/>
                                    </IconButton>
                                    <Stack>
                                        <Typography component="p" variant='caption' fontSize={'12px'} color={'#7b8893'}>
                                            成交客户
                                        </Typography>
                                        <Typography component="h6" variant='subtitle2' fontWeight={600}>
                                            {totalDeal}个
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                            <Box component={'div'} bgcolor={'#eff4fa'} p={'9px'} borderRadius={'12px'}>
                                <Stack direction={'row'} gap={2} alignContent={'center'} alignItems={'center'}>
                                    <IconButton sx={{backgroundColor: '#cce8f9', padding: '8px 14px', borderRadius: '9999px'}} size={'small'}>
                                        <TrendingDownOutlined sx={{color: '#00a1ff'}}/>
                                    </IconButton>
                                    <Stack>
                                        <Typography component="p" variant='caption' fontSize={'12px'} color={'#7b8893'}>
                                            流失客户
                                        </Typography>
                                        <Typography component="h6" variant='subtitle2' fontWeight={600}>
                                            {totalChurn}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
